import { forwardRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Attachment } from "@dreamhub/designsystem/icons";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
// Type-only: AttachmentsField delegates here, so a value import would close
// the cycle at runtime.
import type { AttachmentsFieldProps } from "@/components/controls/AttachmentsField";

// The compact popover form of the file field, kept for the slots that have no
// room for the inline design-system composite: the field is a labelled
// trigger, or the stored files plus an add button once there are any, and the
// picker opens over it rather than in place.
const AttachmentsFieldPopover = forwardRef<
  HTMLDivElement,
  Omit<AttachmentsFieldProps, "variant">
>(
  (
    {
      value,
      onChange,
      name,
      placeholder,
      className,
      accept,
      isSingleFile,
      disabled
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // A stable identity per stored list: FileUpload keys its prune effect on
    // this array, and a fresh [] each render would re-run it on every render.
    const attachments = useMemo(
      () => (Array.isArray(value) ? value : []),
      [value]
    );
    const hasAttachments = attachments.length > 0;

    const handleAttachmentsChange = (updated: SavedAttachmentInfo[]) => {
      onChange(updated);
      // A single-file field has nothing left to do once its upload lands;
      // multi-file stays open for adding more files or reviewing errors.
      if (isSingleFile && updated.length > 0) {
        setIsOpen(false);
      }
    };

    // This slot sits in the modal's metadata strip alongside the assignee,
    // due date, reminders and priority chips, which share this row's bordered
    // chip look — match that rest-state border and fill rather than the
    // borderless treatment the other AttachmentsField variants use, or this
    // is the only chip in the row without one.
    const triggerClassName = cn(
      "border-[0.5px] border-border-border3 bg-badges-default rounded-lg gap-[6px] flex items-center cursor-pointer transition-all duration-200 ease-in-out",
      !isOpen && "hover:!bg-surface-hover-strong",
      isOpen && "bg-white-4 border-primary"
    );

    return (
      <Popover
        modal
        open={isOpen}
        onOpenChange={(open) => {
          // The picker only exists inside the popover, so closing it while a
          // file is still in transit unmounts the uploader: the request still
          // lands on the server, but the completion has nowhere to publish to
          // and the file is never attached, with nothing on screen to say so.
          // Hold the popover open until the upload settles either way — a
          // failure clears this too, so a dead request cannot trap it.
          if (!open && isUploading) return;
          setIsOpen(open);
        }}
      >
        {/* Anchored on the field, not on the trigger: the trigger shrinks to
            an icon once files are listed, and the popover has to keep the
            field's own width and left edge rather than the icon's.
            min-w-0: this sits in a flex value cell that clips overflow, so
            without it a long file list stretches the row and its right edge is
            cut off instead of the file names truncating. */}
        <PopoverAnchor asChild>
          <div
            ref={ref}
            className={cn(
              "relative w-full min-w-0",
              // The due-date/reminders chips carry this same rest/open
              // elevation pair on their own wrapper — omitting the rest
              // state here left this the only chip in the row without it.
              isOpen ? "shadow-field-focus" : "shadow-field",
              className
            )}
          >
            {hasAttachments ? (
              // Matches the sibling due-date/priority/reminders chips: one
              // compact badge in the row, not one per file — the full list
              // with per-file download/remove lives in the popover content.
              <PopoverTrigger asChild>
                {/* Never native-disabled here: a disabled field is read-only,
                    not inert — this button is the only way to open the
                    popover and view the stored files, so disabling it would
                    hide them outright instead of just withholding edits.
                    isReadOnly on the inner FileUpload already withholds the
                    write affordances once it's open. */}
                <button
                  type="button"
                  className={cn(triggerClassName, "px-2 py-[6px] h-9")}
                  aria-label={t("filesUpload.filesAttached", {
                    count: attachments.length
                  })}
                  data-test={`${name}-input`}
                >
                  <IconContainer
                    as="span"
                    color="secondary"
                    className="flex-shrink-0"
                  >
                    <Attachment />
                  </IconContainer>
                  <Text as="span" variant="body">
                    {attachments.length}
                  </Text>
                </button>
              </PopoverTrigger>
            ) : (
              // A semantic button so the picker opens from the keyboard too.
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  // The visible label is the placeholder, which callers may
                  // omit — without a fallback the button is icon-only and
                  // unnamed.
                  aria-label={placeholder || t("filesUpload.addFiles")}
                  className={cn(
                    triggerClassName,
                    "w-full pl-3 pr-2 py-[6px] min-h-9",
                    disabled && "cursor-default"
                  )}
                  data-test={`${name}-input`}
                >
                  <IconContainer
                    as="span"
                    color="secondary"
                    className="flex-shrink-0"
                  >
                    <Attachment />
                  </IconContainer>
                  <Text
                    as="span"
                    variant="bodyTertiary"
                    className="truncate min-w-0"
                  >
                    {placeholder}
                  </Text>
                </button>
              </PopoverTrigger>
            )}
          </div>
        </PopoverAnchor>
        {/* Matching the anchored field width pins the popover to the field's own
            column, so collision handling can never shift it sideways over
            neighboring fields (same approach as MultiSelect). The floor keeps
            the drop zone usable where the field renders in a narrow column. */}
        <PopoverContent
          className="w-[--radix-popper-anchor-width] min-w-[240px]"
          align="start"
          sideOffset={4}
          collisionPadding={8}
        >
          {/* Preview layout inside the popover: the compact upload button and
              file stack, not the Large drag-and-drop frame, which the
              popover has no room for. */}
          <FileUpload
            onAttachmentsChange={handleAttachmentsChange}
            onUploadingChange={setIsUploading}
            initialAttachments={attachments}
            isPreviewLayout
            accept={accept}
            isSingleFile={isSingleFile}
            disabled={disabled}
            isReadOnly={disabled}
            dataTestPrefix={`${name}-file`}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
AttachmentsFieldPopover.displayName = "AttachmentsFieldPopover";

export default AttachmentsFieldPopover;
